import React, { useContext, useEffect, useRef, useState } from "react";
import { useParams } from "react-router-dom";
import { APIContext } from "../../store/APIStore";
import { StoreContext } from "../../store/Store";
import { FaRegFileAlt } from "react-icons/fa";
import NotificationMessage, {
  notificationType,
} from "../../components/NotificationMessage/NotificationMessage";
import FdButton from "../../components/UIComponents/Button/FdButton";
import { IconButton, TextField } from "@mui/material";
import { CloseRounded } from "@mui/icons-material";
import Box from "@mui/material/Box";
import FdTypography from "../../components/UIComponents/Typography/FdTypography";
import SimpleActionDialog, {
  actionDialogType,
} from "../../components/SimpleActionDialog/SimpleActionDialog";
import { theme } from "../../theme";
import { EmptyScreen } from "../../components/EmptyScreen/EmptyScreen";
import { logProcessStyle as classes } from "./styles";

export const LogProcess = () => {
  const { eventId } = useParams() as any;
  const api = useContext(APIContext);
  const [state, dispatch] = useContext(StoreContext);
  const notificationRef = useRef<any>();
  const [file, setFile] = useState<File | null>(null);
  const [confirmationDialog, setConfirmationDialog] = useState<any>({
    open: false,
    title: "",
    type: actionDialogType.REGULAR,
    isLoading: false,
    onConfirm: () => {},
    onCancel: () => {},
  });

  const fetchSelectedEvent = async () => {
    try {
      const event = await api.eventService.getEvent({ id: eventId });
      dispatch({ type: "SET_SELECTED_EVENT", payload: event });
    } catch (error: any) {
      console.log(error.response);
      notificationRef.current?.show(
        error.response.data.message,
        notificationType.Error
      );
    }
  };

  // Handle file input change
  const handleImageSelection = (event: React.ChangeEvent<HTMLInputElement>) => {
    const selectedFile = event.target.files?.[0] || null;
    if (selectedFile) {
      const fileExtension = selectedFile.name.split(".").pop();
      if (fileExtension && fileExtension.toLowerCase() === "fda") {
        setFile(selectedFile);
      } else {
        setFile(null);
        // show notification
        notificationRef.current?.show(
          "Please choose only '.fda' file",
          notificationType.Error
        );
      }
    }
  };

  const handleSubmit = async (event: React.FormEvent) => {
    event.preventDefault();

    try {
      if (!file) {
        notificationRef.current?.show(
          "Please select a file to upload.",
          notificationType.Error
        );
        return;
      }
      // Show loading dialog
      setConfirmationDialog({
        ...confirmationDialog,
        open: true,
        title: "Uploading .fda file",
        isLoading: true,
      });
      const formData = new FormData();
      formData.append("file", file);
      formData.append("fileName", file.name);
      if (file) {
        const fileData = (await api.fileService.uploadLogProcess(
          eventId,
          file.name,
          file
        )) as any;
        if (fileData) {
          try {
            const filePath = fileData.filePath;
            await api.attendeeService.sendActionLogs(eventId, filePath);
            notificationRef.current?.show(
              "Attendee action logs updated successfully",
              notificationType.Success
            );
            setConfirmationDialog({
              ...confirmationDialog,
              open: false,
              isLoading: true, // isloading will be true to prevent flicker.
            });
            setFile(null);
          } catch (error: any) {
            console.log(error.response);
            notificationRef.current?.show(
              error.response.data.detail || "Error",
              notificationType.Error
            );

            // Hide dialog on error
            setConfirmationDialog({
              ...confirmationDialog,
              open: false,
              isLoading: true,
            });
          }
        }
      }

      // show notification
    } catch (error: any) {
      console.log(error.response);
      setConfirmationDialog({
        ...confirmationDialog,
        open: false,
        isLoading: true, // isloading will be true to prevent flicker.
      });
      notificationRef.current?.show(
        error.response.data.message || "error",
        notificationType.Error
      );
    }
  };

  useEffect(() => {
    if (eventId && !state.selectedEvent) {
      fetchSelectedEvent();
    }
  }, []);

  return (
    <>
      <FdTypography variant="h2" sx={{ mb: 2 }}>
        Upload Log process
      </FdTypography>
      <Box sx={classes.whiteBoard}>
        <Box>
          <form onSubmit={handleSubmit}>
            {!file ? (
              <>
                <EmptyScreen
                  message="No file selected"
                  icon={
                    <FaRegFileAlt
                      size="50px"
                      color={theme.palette.primary.main}
                    />
                  }
                />
                <label htmlFor="file-input">
                  <FdButton variant="contained" component="span" sx={{ mt: 3 }}>
                    Select File
                  </FdButton>
                </label>
                <input
                  type="file"
                  hidden
                  onChange={handleImageSelection}
                  id="file-input"
                />
              </>
            ) : (
              <>
                <Box>
                  <FaRegFileAlt size="100px" color={theme.palette.grey[700]} />
                </Box>
                <Box sx={{ mt: 2 }}>
                  <FdTypography label={file.name} component="span" />
                  <IconButton
                    onClick={() => setFile(null)}
                    sx={{ background: theme.palette.grey[700], ml: 2 }}
                    size="small"
                  >
                    <CloseRounded sx={{ height: "14px", width: "14px" }} />
                  </IconButton>
                </Box>

                <Box>
                  <FdButton variant="contained" type="submit" sx={{ mt: 3 }}>
                    Upload File
                  </FdButton>
                </Box>
              </>
            )}
          </form>
        </Box>
      </Box>

      <NotificationMessage ref={notificationRef} />
      <SimpleActionDialog
        type={confirmationDialog.type}
        icon={confirmationDialog.icon}
        open={confirmationDialog.open}
        title={confirmationDialog.title}
        isLoading={confirmationDialog.isLoading}
        onConfirm={confirmationDialog.onConfirm}
        onCancel={confirmationDialog.onCancel}
        content={confirmationDialog.content}
        confirmLabel={confirmationDialog.confirmLabel}
        maxWidth={confirmationDialog.maxWidth}
        cancelLabel={confirmationDialog.cancelLabel}
      />
    </>
  );
};
